import * as models from "@common/models/v2";
import type * as schemas from "@common/schemas/v2";
import { BaseStep } from "./base";
import { ElementTextWriter } from "@/lib/element";
import { StepActions } from "./step";
import { Deferred } from "@/lib/Deferred";
import { makeObservable, observable, override } from "mobx";
import { FlowFlexibility } from "@common/schemas";

export class AutoTypeStep extends BaseStep {
  attributes: models.AutoTypeStepAttributes;
  actions: StepActions;

  target?: Element | null;
  isTyped: boolean = false;
  elementWriter: ElementTextWriter;
  deferredElementFound: Deferred<Element | null> | null = null;
  stopTrackingTargetElement?: () => void;

  constructor(step: schemas.Step, actions: StepActions, flowFlexibility: FlowFlexibility) {
    super(step, actions, flowFlexibility);
    this.attributes = new models.AutoTypeStepAttributes(step.attributes as schemas.AutoTypeStepAttributes);
    this.actions = actions;
    this.elementWriter = new ElementTextWriter();
    makeObservable(this, {
      isTyped: observable,
      isBlocking: override,
    });
  }

  async init() {
    super.init();
    this.isTyped = false;
    this.deferredElementFound = new Deferred<Element | null>();

    this.stopTrackingTargetElement?.();
    this.trackTargetElement();
  }

  private trackTargetElement() {
    this.stopTrackingTargetElement?.();
    this.stopTrackingTargetElement = this.actions.onTargetElementChange(
      this.id,
      this.handleTargetElementChange.bind(this)
    );
  }

  private handleTargetElementChange(targetElement: Element | null) {
    if (!targetElement) {
      return;
    }

    this.target = targetElement;
    this.actions.setCurrentTargetElement(targetElement);
    this.deferredElementFound?.resolve(targetElement);
  }

  async finish() {
    super.finish();
    await this.write(this.attributes.text);
  }

  dispose(): void {
    super.dispose();

    this.stopTrackingTargetElement?.();
    this.stopTrackingTargetElement = undefined;
    this.target = undefined;
    this.actions.setCurrentTargetElement(undefined);
  }

  private async write(text: string) {
    if (this.deferredElementFound) {
      await this.deferredElementFound?.promise;
    }

    if (!this.target) {
      return;
    }

    await this.elementWriter.writeText(this.target as HTMLInputElement, text, this.attributes.instantPaste);
    this.isTyped = true;
  }

  get isBlocking() {
    return !this.isTyped;
  }

  get isBlockingStepType() {
    return true;
  }

  get isTargetableStepType() {
    return true;
  }
}
